/* eslint-disable */
/*eslint-disable*/
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
  Collapse,
  Box,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import {
  features,
  language,
  otc,
  prescription as prescript,
  condition,
  countries,
  default_country_code,
  Google_Map_Key,
} from "config";

import DocumentScanner from "../../components/DocumentScanner";
import FileUploader from "components/FileUploader";

const useStyles = makeStyles((theme) => ({
  collapsibleContainer: {
    backgroundColor: "#f9f9f9",
    padding: theme.spacing(2),
    borderRadius: "4px",
    marginTop: theme.spacing(1),
    border: "1px solid #ddd",
  },
  collapsibleField: {
    marginBottom: theme.spacing(1.5),
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#bbb",
      },
      "&:hover fieldset": {
        borderColor: "#888",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#009ad8",
      },
    },
  },
  expandIcon: {
    transition: "transform 0.3s ease",
  },
  expandedIcon: {
    transform: "rotate(180deg)",
  },
  AddUserTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#009ad8",
    color: "#fff",
    padding: "16px",
    borderRadius: "4px 4px 0 0",
    fontSize: "18px",
    fontWeight: "bold",
  },
  selectField: {
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    border: "1px solid #ccc",
    padding: "10px 12px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#ccc",
      },
      "&:hover fieldset": {
        borderColor: "#999",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#009ad8",
      },
    },
  },
  userTypeField: {
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    border: "1px solid #ccc",
    padding: "10px 12px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#ccc",
      },
      "&:hover fieldset": {
        borderColor: "#999",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#009ad8",
      },
    },
  },
  saveButton: {
    fontWeight: "bold",
    color: "#ffffff",
    backgroundColor: "#009ad8",
    "&:hover": {
      backgroundColor: "#2c6e31",
    },
  },
  closeButton: {
    backgroundColor: "#009ad8",
    color: "#fff",
    textTransform: "none",
    "&:disabled": {
      backgroundColor: "#cccccc",
      color: "#666666",
    },
  },
  dialogContent: {
    backgroundColor: "#fafafa",
    padding: "20px",
  },
  formControl: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  textFieldNotes: {
    border: "1px solid #ccc",
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    "& .MuiInputBase-input": {
      padding: "12px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#ccc",
        borderTopWidth: "2px", // thicker top border for visibility
      },
      "&:hover fieldset": {
        borderColor: "#999",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#009ad8",
      },
    },
  },
  textField: {
    backgroundColor: "#ffffff",
    borderRadius: "4px",
    "& .MuiInputBase-input": {
      padding: "12px",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#ccc",
        borderTopWidth: "2px", // thicker top border for visibility
      },
      "&:hover fieldset": {
        borderColor: "#999",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#009ad8",
      },
    },
  },
  dialogActions: {
    padding: "16px",
    justifyContent: "space-between",
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "6px",
    display: "block",
    transition: "0.2s ease all",
  },
  floatingLabel: {
    transform: "translate(14px, -12px) scale(0.75)",
    backgroundColor: "#ffffff",
    padding: "0 4px",
    color: "#009ad8",
  },
  formHelperText: {
    fontSize: "12px",
    color: "#d32f2f",
  },
  input: {
    fontSize: "14px",
    padding: "10px 12px",
  },
  inputField: {
    marginBottom: theme.spacing(1.5),
  },
}));

// --- Simple helper to load Google Maps JS API (returns promise that resolves when window.google available)
function loadGoogleMapsApi(apiKey) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("No window"));
    if (window.google && window.google.maps) return resolve(window.google);
    const id = "gmaps-script";
    if (document.getElementById(id)) {
      // script present but maybe still loading
      const wait = () => {
        if (window.google && window.google.maps) resolve(window.google);
        else setTimeout(wait, 200);
      };
      wait();
      return;
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) resolve(window.google);
      else reject(new Error("Google loaded but window.google missing"));
    };
    script.onerror = () => reject(new Error("Failed to load Google Maps script"));
    document.head.appendChild(script);
  });
}

// --- MapPicker component used only for picking address coords + formatted address
function MapPicker({ open, onClose, initialCoords = null, initialAddress = "", onPick }) {
  const containerRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [formattedAddress, setFormattedAddress] = useState(initialAddress || "");
  const [coords, setCoords] = useState(initialCoords || null);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);

    loadGoogleMapsApi(Google_Map_Key)
      .then((google) => {
        if (!mounted) return;
        geocoderRef.current = new google.maps.Geocoder();
        const defaultCenter = initialCoords || { lat: -26.2041, lng: 28.0473 }; // fallback
        const map = new google.maps.Map(containerRef.current, { center: defaultCenter, zoom: 13 });

        const mk = new google.maps.Marker({ position: defaultCenter, map, draggable: true });
        markerRef.current = mk;

        if (initialCoords) {
          map.setCenter(initialCoords);
          setCoords(initialCoords);
          tryReverseGeocode(initialCoords);
        }

        map.addListener("click", (e) => {
          const p = { lat: e.latLng.lat(), lng: e.latLng.lng() };
          markerRef.current.setPosition(p);
          setCoords(p);
          tryReverseGeocode(p);
        });

        mk.addListener("dragend", () => {
          const p = { lat: mk.getPosition().lat(), lng: mk.getPosition().lng() };
          setCoords(p);
          tryReverseGeocode(p);
          map.panTo(p);
        });

        // Places Autocomplete on search input
        const input = document.getElementById("mappicker-search-input");
        if (input) {
          const ac = new google.maps.places.Autocomplete(input, { fields: ["formatted_address", "geometry", "name"] });
          autocompleteRef.current = ac;
          ac.addListener("place_changed", () => {
            const place = ac.getPlace();
            if (!place || !place.geometry) return;
            const p = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
            map.panTo(p);
            markerRef.current.setPosition(p);
            setCoords(p);
            setFormattedAddress(place.formatted_address || place.name || "");
          });
        }

        setLoading(false);
      })
      .catch((err) => {
        console.error("Map load error:", err);
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [open, initialCoords]);

  const tryReverseGeocode = (p) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ location: p }, (results, status) => {
      if (status === "OK" && results && results[0]) setFormattedAddress(results[0].formatted_address || "");
      else setFormattedAddress(`${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`);
    });
  };

  const handleUse = () => {
    if (!coords) return;
    onPick({ formattedAddress: formattedAddress || `${coords.lat}, ${coords.lng}`, coords });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Pick address on map</DialogTitle>
      <DialogContent style={{ height: 480 }}>
        <Box mb={1}>
          <TextField id="mappicker-search-input" placeholder="Search places (optional)" fullWidth variant="outlined" />
        </Box>
        <div ref={containerRef} style={{ width: "100%", height: 360, borderRadius: 6 }} />
        <Box mt={1}>
          <Typography variant="body2">Selected: {formattedAddress || (coords ? `${coords.lat}, ${coords.lng}` : "none")}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleUse} color="primary" variant="contained" disabled={!coords}>
          Use this address
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const formatDateDDMMYY = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d)) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const FormComponent = ({ data = {}, columns = [], modalType, onClose, onSubmit }) => {
  const classes = useStyles();
  const auth = useSelector((state) => state.auth);
  const specialities = useSelector((state) => state.specialitytypes.specialities);
  const doctortypes = useSelector((state) => state.doctortypes?.doctortypes || []);
  const [specialityTypes, setSpecialityTypes] = useState([]);
  const [addressExpanded, setAddressExpanded] = useState(false);
  const [otcT, setotcT] = useState([]);
  const [prescripts, setprescripts] = useState([]);
  const [conditions, setconditions] = useState([]);
  const [consentFormFile, setConsentFormFile] = useState(null);

  const [countryCode, setCountryCode] = useState("+" + default_country_code.phone);
  const [doctorTypes, setDoctorTypes] = useState([]);

  const [addressPickerOpen, setAddressPickerOpen] = useState(false);

  const formatCountries = () =>
    countries.map((country) => ({
      label: `${country.label} (+${country.phone})`,
      value: `+${country.phone}`,
      key: country.code,
    }));

  const flattenColumns = (cols) => {
    const flat = [];
    cols.forEach((col) => {
      if (col.id === "TenantDetails") {
        flat.push(...flattenColumns(col.columns));
      } else {
        flat.push(col);
      }
    });
    return flat;
  };

  const flattenedColumns = flattenColumns(columns);

  const getNestedValue = (obj, path) => {
    if (!path || path === "username") return "";
    return path.split(".").reduce((acc, key) => (acc ? acc[key] : ""), obj);
  };

  const initialFields = flattenedColumns
    .filter(
      (field) =>
        !field.enableHiding &&
        field.accessorKey &&
        field.accessorKey.toLowerCase() !== "username" &&
        field.accessorKey.toLowerCase() !== "id"
    )
    .map((field) => ({
      ...field,
      // if address in data is an object leave it as-is; otherwise it will be a string
      value: modalType === "edit" ? getNestedValue(data, field.accessorKey) || "" : "",
      isEditable: field.enableEditing !== false,
    }));

  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (otc) {
      const arr = {};
      otc.forEach((o) => (arr[o.concept_name] = o.concept_name));
      setotcT(Object.values(arr));
    }
    if (prescript) {
      const arr = {};
      prescript.forEach((p) => (arr[p.concept_name] = p.concept_name));
      setprescripts(Object.values(arr));
    }
    if (condition) {
      const arr = {};
      condition.forEach((c) => (arr[c.concept_name] = c.concept_name));
      setconditions(Object.values(arr));
    }
  }, []);

  useEffect(() => {
    if (doctortypes && Array.isArray(doctortypes)) setDoctorTypes(doctortypes);
  }, [doctortypes]);

  useEffect(() => {
    if (specialities && Array.isArray(specialities)) setSpecialityTypes(specialities);
  }, [specialities]);

  const handleInputChange = (accessorKey, value) => {
    setFields((prevFields) => prevFields.map((field) => (field.accessorKey === accessorKey ? { ...field, value } : field)));
  };

  // map picker open/close
  const openAddressPicker = () => setAddressPickerOpen(true);
  const closeAddressPicker = () => setAddressPickerOpen(false);
  const onAddressPicked = (addressObj) => {
    // addressObj: { formattedAddress, coords: { lat, lng } }
    handleInputChange("address", { formattedAddress: addressObj.formattedAddress, coords: addressObj.coords });
  };




const handleSubmit = async () => {
  console.log("handleSubmit: START", {
    timestamp: new Date().toISOString(),
    dataSnapshot: data,
    fieldsCount: Array.isArray(fields) ? fields.length : 0,
  });

  setLoading(true);

  // Build a payload that contains every accessorKey from fields (no omissions).
  const payload = {};

  fields.forEach((field) => {
    try {
      const key = field.accessorKey;

      // address gets special handling (object comparison), but we still include the key
      if (key === "address") {
        const orig = getNestedValue(data, "address") || null;
        const newVal = field.value || null;
        console.log("handleSubmit: checking address", { orig, newVal });

        // If there is a new object use it, otherwise if existing data has it use that, else null
        payload.address = newVal !== null ? newVal : (orig !== null ? orig : null);
        console.log("handleSubmit: payload.address ->", payload.address);
        return;
      }

      // Normalise arrays vs scalars: keep arrays intact
      const newVal = Array.isArray(field.value) ? field.value : field.value;

      // If newVal is undefined or null, we default to an empty string (so key is present)
      if (newVal === undefined || newVal === null) {
        // Try to keep an existing value from `data` if present; otherwise empty string
        const existing = data ? getNestedValue(data, key) : undefined;
        payload[key] = existing !== undefined && existing !== null ? existing : "";
      } else {
        payload[key] = newVal;
      }

      console.log("handleSubmit: set payload field", { key, value: payload[key] });
    } catch (err) {
      console.error("handleSubmit: error while processing field", field, err);
      // ensure key exists even on error
      try {
        if (field && field.accessorKey) payload[field.accessorKey] = "";
      } catch (_) {}
    }
  });

  // phone normalization & ensure phone key exists in payload
  try {
    const phoneField = fields.find((f) => f.accessorKey === "phone");
    if (phoneField) {
      let phoneValue = (phoneField.value || "").toString().trim();
      if (phoneValue && !phoneValue.startsWith(countryCode)) phoneValue = countryCode + phoneValue;
      // fallback to existing data.phone or empty string if nothing
      payload.phone = phoneValue || (data && data.phone ? data.phone : "");
      console.log("handleSubmit: normalized phone ->", payload.phone);
    } else if (data && data.phone !== undefined) {
      // ensure we don't drop phone from data if it's not part of fields but exists in data
      payload.phone = data.phone;
    }
  } catch (err) {
    console.error("handleSubmit: phone processing error", err);
    payload.phone = payload.phone ?? "";
  }

  // mobile normalization & ensure mobile key exists in payload
  try {
    const mobileField = fields.find((f) => f.accessorKey === "mobile");
    if (mobileField) {
      let mobileValue = (mobileField.value || "").toString().trim();
      if (mobileValue && !mobileValue.startsWith(countryCode)) mobileValue = countryCode + mobileValue;
      payload.mobile = mobileValue || (data && data.mobile ? data.mobile : "");
      console.log("handleSubmit: normalized mobile ->", payload.mobile);
    } else if (data && data.mobile !== undefined) {
      payload.mobile = data.mobile;
    }
  } catch (err) {
    console.error("handleSubmit: mobile processing error", err);
    payload.mobile = payload.mobile ?? "";
  }

  // consent form if present
  if (consentFormFile) {
    payload.consentForm = consentFormFile;
    console.log("handleSubmit: consentFormFile present -> added to payload");
  } else {
    // ensure key exists (empty string) if consentForm is part of fields or previously present
    if (fields.some((f) => f.accessorKey === "consentForm") || (data && data.consentForm !== undefined)) {
      payload.consentForm = data && data.consentForm !== undefined ? data.consentForm : "";
    }
  }

  // Ensure every field accessorKey is present in the final payload (defensive safety)
  fields.forEach((f) => {
    if (!(f.accessorKey in payload)) {
      payload[f.accessorKey] = "";
    }
  });

  console.log("handleSubmit: final payload (only passed columns included, all keys present):", payload);

  const dataPayload={
    ...payload,
    practiceNumber:auth.info.profile.practiceNumber
  }

  try {
    const result = onSubmit(dataPayload);

    if (result && typeof result.then === "function") {
      console.log("handleSubmit: onSubmit returned a Promise, awaiting...");
      const resolved = await result;
      console.log("handleSubmit: onSubmit resolved:", resolved);
    } else {
      console.log("handleSubmit: onSubmit returned (sync):", result);
    }
  } catch (err) {
    console.error("handleSubmit: onSubmit threw an error:", err);
  } finally {
    try {
      onClose();
      console.log("handleSubmit: onClose called");
    } catch (err) {
      console.error("handleSubmit: onClose error:", err);
    }
    setLoading(false);
    console.log("handleSubmit: FINISHED", { timestamp: new Date().toISOString() });
  }
};



  const toggleAddress = () => setAddressExpanded(!addressExpanded);

  // helper: render a specific control for common practice fields
  const renderFieldControl = (field) => {
    const key = field.accessorKey;

    // Address - show preview + pick on map button
    if (key === "address") {
      const val = field.value || "";
      const preview =
        typeof val === "string"
          ? val
          : val && val.formattedAddress
          ? val.formattedAddress
          : val && val.coords
          ? `${val.coords.lat.toFixed(5)}, ${val.coords.lng.toFixed(5)}`
          : "";
      return (
        <Box display="flex" gap={1} alignItems="center">
          <TextField label={field.header || "Address"} variant="outlined" fullWidth value={preview} InputProps={{ readOnly: true }} />
          <Button variant="contained" color="primary" onClick={() => openAddressPicker()} style={{ minWidth: 160 }}>
            Pick on map
          </Button>
        </Box>
      );
    }

    // practiceNumber - keep disabled / from profile
    if (key === "practiceNumber") {
      const val = field.value || auth?.info?.profile?.practiceNumber || "";
      return (
        <TextField label={field.header || "Practice Number"} variant="outlined" fullWidth value={val} disabled className={classes.textField} />
      );
    }

    // specialityType - single-select searchable
    if (key === "specialityType") {
      return (
        <Autocomplete
          options={Array.isArray(specialityTypes) ? specialityTypes : []}
          getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.value || "")}
          value={field.value || ""}
          onChange={(_, newVal) => handleInputChange(key, newVal || "")}
          freeSolo
          renderInput={(params) => <TextField {...params} label={field.header || "Service Type"} variant="outlined" fullWidth />}
        />
      );
    }

    // doctorTypes - single-select searchable
    if (key === "doctorType" || key === "doctorTypes") {
      return (
        <Autocomplete
          options={Array.isArray(doctorTypes) ? doctorTypes : []}
          getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label || opt?.name || "")}
          value={field.value || ""}
          onChange={(_, newVal) => handleInputChange(key, newVal || "")}
          freeSolo
          renderInput={(params) => <TextField {...params} label={field.header || "Doctor Type"} variant="outlined" fullWidth />}
        />
      );
    }

    // convenience_fee_type - select flat/percentage
    if (key === "convenience_fee_type") {
      return (
        <FormControl fullWidth variant="outlined">
          <InputLabel id={`${key}-label`}>{field.header || "Convenience Fee Type"}</InputLabel>
          <Select labelId={`${key}-label`} value={field.value || ""} onChange={(e) => handleInputChange(key, e.target.value)} label={field.header || "Convenience Fee Type"}>
            <MenuItem value={"flat"}>{language.flat}</MenuItem>
            <MenuItem value={"percentage"}>{language.percentage}</MenuItem>
          </Select>
        </FormControl>
      );
    }

    // numeric fields
    const numericKeys = ["base_fare", "base_rate", "convenience_fees", "dispensing_fee", "min_charge", "min_fare", "rate_per_unit_distance", "rate_per_hour"];
    if (numericKeys.includes(key)) {
      return (
        <TextField label={field.header} variant="outlined" fullWidth value={field.value || ""} onChange={(e) => handleInputChange(key, e.target.value)} type="number" inputProps={{ step: "0.01" }} className={classes.textField} />
      );
    }

    // consultation_duration
    if (key === "consultation_duration") {
      const durations = [10, 15, 20, 30, 45, 60];
      return (
        <FormControl fullWidth variant="outlined">
          <InputLabel id={`${key}-label`}>{field.header || "Consultation Duration (min)"}</InputLabel>
          <Select labelId={`${key}-label`} value={field.value || ""} onChange={(e) => handleInputChange(key, e.target.value)} label={field.header || "Consultation Duration (min)"}>
            {durations.map((d) => (
              <MenuItem key={d} value={d}>
                {d} minutes
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // default: text field or multiline
    return (
      <TextField label={field.header} variant="outlined" name={field.accessorKey} value={field.value || ""} onChange={(e) => handleInputChange(field.accessorKey, e.target.value)} error={Boolean(errors[field.accessorKey])} helperText={errors[field.accessorKey]} className={classes.textField} fullWidth multiline={field.multiline} minRows={field.minRows} maxRows={field.maxRows} />
    );
  };

  return (
    <>
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle className={classes.AddUserTitle}>{modalType === "edit" ? "Edit " : "Add "} Practice</DialogTitle>

        <DialogContent className={classes.dialogContent}>
          {/* Address replaced by map picker button + preview above — no multi text fields */}
          {fields.some((field) => field.accessorKey === "address") && (
            <>
              <Button onClick={toggleAddress} fullWidth variant="contained" style={{ marginBottom: "10px", backgroundColor: "#f1f1f1", textAlign: "left", padding: "10px" }}>
                Address Details
              </Button>
              <Collapse in={addressExpanded} className={classes.collapsibleContainer} style={{ border: "1px solid #ddd", padding: "10px" }}>
                {/* previously multi-line address inputs removed — we show the map picker control instead */}
                <Box mb={1}>
                  <Typography variant="body2" style={{ marginBottom: 8 }}>
                    Use the map picker to set the primary address (stores both formatted address and coords).
                  </Typography>
                  {/* render preview & button */}
                  {fields
                    .filter((f) => f.accessorKey === "address")
                    .map((f) => (
                      <Box key="address-preview" display="flex" gap={1} alignItems="center">
                        <TextField label="Address" variant="outlined" fullWidth value={typeof f.value === "string" ? f.value : f.value && f.value.formattedAddress ? f.value.formattedAddress : f.value && f.value.coords ? `${f.value.coords.lat.toFixed(5)}, ${f.value.coords.lng.toFixed(5)}` : ""} InputProps={{ readOnly: true }} />
                        <Button variant="contained" color="primary" onClick={() => setAddressPickerOpen(true)} style={{ minWidth: 160 }}>
                          Pick on map
                        </Button>
                      </Box>
                    ))}
                </Box>
              </Collapse>
            </>
          )}

          {/* Special multi-select or special keys */}
          {["drug_concept_id", "drug_source_value", "route_source_value", "devicetype", "doctorTypes", "specialityTypes"].map((key) => {
            let options = [];

            if (key === "drug_concept_id") {
              options = otcT;
            } else if (key === "drug_source_value") {
              options = prescripts;
            } else if (key === "route_source_value") {
              options = conditions;
            } else if (key === "devicetype") {
              options = ["ALL", "ANDROID", "IOS"];
            } else if (key === "doctorTypes") {
              options = doctorTypes;
            } else if (key === "specialityTypes") {
              options = specialityTypes;
            }

            return fields.some((field) => field.accessorKey === key) ? (
              <Autocomplete
                key={key}
                multiple
                freeSolo
                options={Array.isArray(options) ? options : ["No option"]}
                value={fields.find((field) => field.accessorKey === key)?.value || []}
                onChange={(_, newValue) => handleInputChange(key, newValue)}
                renderInput={(params) => <TextField {...params} label={key.replaceAll("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())} variant="outlined" fullWidth className={classes.textField} />}
                className={classes.formControl}
              />
            ) : null;
          })}

          {fields.some((field) => field.accessorKey === "consentForm") && (
            <div className={classes.formControl}>
              <label className={classes.label}>Consent Form Upload</label>
              <FileUploader onFileSelect={(file) => setConsentFormFile(file)} acceptedTypes={["application/pdf", "image/*"]} />
            </div>
          )}

          {fields.some((field) => field.accessorKey === "uploadDocument") && (
            <div className={classes.formControl}>
              <label className={classes.label}>Document Upload</label>
              <FileUploader onFileSelect={(file) => setConsentFormFile(file)} acceptedTypes={["application/pdf", "image/*"]} />
            </div>
          )}

          {fields.some((field) => field.accessorKey === "note_text") && (
            <TextField label="Note" variant="outlined" name="note_text" fullWidth multiline minRows={2} maxRows={8} className={classes.textFieldNotes} style={{ marginBottom: 8 }} value={fields.find((field) => field.accessorKey === "note_text")?.value || ""} onChange={(e) => handleInputChange("note_text", e.target.value)} InputProps={{ style: { overflowY: "auto" } }} />
          )}

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {fields
              .filter((field) => !["devicetype", "drug_concept_id", "doctorTypes", "route_source_value", "person_id", "drug_source_value", "userType", "specialityTypes", "note_text", "consentForm", "uploadDocument"].includes(field.accessorKey))
              .map((field) => (
                <FormControl key={field.accessorKey} className={classes.formControl} error={Boolean(errors[field.accessorKey])} fullWidth style={{ marginBottom: 12 }}>
                  {(field.accessorKey === "mobile" || field.accessorKey === "phone") && (
                    <>
                      <InputLabel className={classes.label}>Country</InputLabel>
                      <Select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} className={classes.inputField}>
                        {formatCountries().map((country) => (
                          <MenuItem key={country.key} value={country.value}>
                            {country.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}

                  {field.type === "date" ? (
                    <DatePicker label={field.header} value={field.value ? new Date(field.value) : null} onChange={(newValue) => handleInputChange(field.accessorKey, newValue ? formatDateDDMMYY(newValue) : "")} renderInput={(params) => <TextField {...params} variant="outlined" fullWidth className={classes.textField} error={Boolean(errors[field.accessorKey])} helperText={errors[field.accessorKey]} InputLabelProps={{ shrink: true }} />} />
                  ) : (
                    // use custom renderer for certain practice keys
                    renderFieldControl(field)
                  )}
                </FormControl>
              ))}
          </LocalizationProvider>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button variant="contained" onClick={handleSubmit} className={classes.saveButton} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : "Save"}
          </Button>
          <Button variant="outlined" onClick={onClose} className={classes.closeButton} disabled={isLoading}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map picker dialog */}
      <MapPicker
        open={addressPickerOpen}
        onClose={() => setAddressPickerOpen(false)}
        // initialCoords: if current address is object with coords, pass it
        initialCoords={
          (() => {
            const addrField = fields.find((f) => f.accessorKey === "address");
            if (!addrField) return null;
            const val = addrField.value;
            if (val && typeof val === "object" && val.coords) return val.coords;
            return null;
          })()
        }
        initialAddress={
          (() => {
            const addrField = fields.find((f) => f.accessorKey === "address");
            if (!addrField) return "";
            const val = addrField.value;
            if (val && typeof val === "object" && val.formattedAddress) return val.formattedAddress;
            if (typeof val === "string") return val;
            return "";
          })()
        }
        onPick={onAddressPicked}
      />
    </>
  );
};

FormComponent.propTypes = {
  data: PropTypes.object,
  columns: PropTypes.array,
  modalType: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default FormComponent;
